import { jsx, jsxs } from "react/jsx-runtime";
import { PromptInput, PromptInputBody, PromptInputTextarea } from "../../../ai-elements/prompt-input.js";
import { ChatInput } from "@brandclub/bc-widgets";
import { useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { AttachmentsRow } from "./AttachmentsRow.js";
import { AttachmentButton } from "./AttachmentButton.js";
import { MicrophoneButton } from "./MicrophoneButton.js";
import { SubmitButton } from "./SubmitButton.js";
import { buildPromptInputStyles } from "./styles.js";
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const PromptInputConcierge = ({
  placeholder = "Ask anything",
  onSubmit,
  disabled = false,
  isStreaming = false,
  onStop,
  value,
  onChange,
  expandDirection = "up",
  maxHeight,
  widgetId = "concierge-chat-input",
  displayName = "Chat Input",
  initialState
}) => {
  const textareaRef = useRef(null);
  const [hasAttachments, setHasAttachments] = useState(false);
  const isMultiline = ((value == null ? void 0 : value.length) ?? 0) > 40 || ((value == null ? void 0 : value.includes("\n")) ?? false);
  const handleSubmit = useCallback(
    async (message) => {
      var _a;
      if (!((_a = message.text) == null ? void 0 : _a.trim()) && (!message.files || message.files.length === 0)) {
        return;
      }
      const fileParts = [];
      if (message.files && message.files.length > 0) {
        for (const file of message.files) {
          if (!ACCEPTED_IMAGE_TYPES.includes(file.mediaType || "")) {
            toast.error("Please upload a valid image file (JPEG, PNG, WebP, or GIF)");
            return;
          }
          if (file.url) {
            if (file.url.startsWith("data:") || file.url.startsWith("http")) {
              fileParts.push({ type: "file", mediaType: file.mediaType || "image/jpeg", url: file.url });
            } else if (file.url.startsWith("blob:")) {
              try {
                const resp = await fetch(file.url);
                const blob = await resp.blob();
                const dataUrl = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });
                fileParts.push({ type: "file", mediaType: file.mediaType || blob.type || "image/jpeg", url: dataUrl });
              } catch (error) {
                console.error("Failed to read blob URL:", error);
                toast.error("Failed to read image file");
                return;
              }
            }
          }
        }
      }
      onSubmit(message.text || "", fileParts.length > 0 ? fileParts : void 0);
    },
    [onSubmit]
  );
  const handleError = useCallback((err) => {
    if (err.code === "max_file_size") {
      toast.error("File size exceeds 10MB limit");
    } else if (err.code === "accept") {
      toast.error("Please upload a valid image file");
    } else {
      toast.error(err.message);
    }
  }, []);
  return /* @__PURE__ */ jsx(ChatInput, { widgetId, displayName, initialState, children: (styles, themeIcons, resolveColor) => {
    const s = styles;
    const isExpandedPreset = s.minHeight >= 100;
    const isExpanded = isMultiline || hasAttachments || isExpandedPreset;
    const borderRadius = isExpanded ? `${styles.borderRadiusMultiLine}px` : `${styles.borderRadiusSingleLine}px`;
    const borderWidth = styles.borderWidth ?? 1;
    const borderColor = resolveColor(styles.borderColor);
    const borderAsShadow = `0 0 0 ${borderWidth}px ${borderColor}`;
    const combinedShadow = styles.shadowEnabled && styles.shadow ? `${styles.shadow}, ${borderAsShadow}` : borderAsShadow;
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    const responsiveStyles = {
      ...styles,
      iconSize: isMobile && s.mobile_iconSize ? s.mobile_iconSize : styles.iconSize,
      submitButtonSize: isMobile && s.mobile_submitButtonSize ? s.mobile_submitButtonSize : styles.submitButtonSize,
      iconGap: isMobile && s.mobile_iconGap ? s.mobile_iconGap : s.iconGap ?? 8
    };
    return /* @__PURE__ */ jsx("div", { className: "tw:mx-auto tw:w-full", style: { maxWidth: `${styles.maxWidth}px` }, children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: "concierge-prompt-input",
        style: {
          borderRadius,
          backgroundColor: resolveColor(styles.backgroundColor),
          boxShadow: combinedShadow,
          transition: "border-radius 0.15s ease-out"
        },
        children: [
          /* @__PURE__ */ jsx("style", { children: buildPromptInputStyles({
            focusBorderColor: resolveColor(styles.borderColorFocus),
            fontSize: styles.fontSize,
            fontWeight: styles.fontWeight,
            fontFamilyCss: s.fontFamilyCss || "inherit",
            textColor: resolveColor(styles.textColor),
            placeholderColor: resolveColor(styles.placeholderColor),
            submitBgColor: resolveColor(styles.submitButtonBgColor),
            submitBgColorHover: resolveColor(styles.submitButtonBgColorHover),
            mobileFontSize: s.mobile_fontSize ?? styles.fontSize,
            mobilePaddingY: s.mobile_paddingY ?? styles.paddingY,
            mobilePaddingX: s.mobile_paddingX ?? styles.paddingX,
            mobileMinHeight: s.mobile_minHeight ?? s.minHeight ?? 0,
            mobileIconGap: s.mobile_iconGap ?? s.iconGap ?? 8,
            mobileIconSize: s.mobile_iconSize ?? styles.iconSize,
            mobileSubmitButtonSize: s.mobile_submitButtonSize ?? styles.submitButtonSize
          }) }),
          /* @__PURE__ */ jsx(
            PromptInput,
            {
              onSubmit: handleSubmit,
              multiple: true,
              globalDrop: true,
              accept: "image/*",
              maxFiles: 5,
              maxFileSize: MAX_FILE_SIZE,
              onError: handleError,
              className: "tw:w-full",
              children: /* @__PURE__ */ jsxs(PromptInputBody, { className: "tw:flex tw:flex-col tw:w-full", children: [
                /* @__PURE__ */ jsx(AttachmentsRow, { onAttachmentsChange: setHasAttachments, styles }),
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: "tw:flex tw:w-full concierge-input-container",
                    style: {
                      flexDirection: isExpanded ? "column" : "row",
                      alignItems: isExpanded ? "stretch" : "center",
                      ...s.minHeight > 0 ? { minHeight: `${s.minHeight}px` } : void 0,
                      padding: isExpanded ? `${styles.paddingY}px ${styles.paddingX}px` : void 0,
                      boxSizing: "border-box",
                      transition: "all 0.2s ease-out"
                    },
                    children: [
                      /* @__PURE__ */ jsx(
                        "div",
                        {
                          className: isExpanded ? "tw:flex-1 tw:min-w-0" : "tw:flex-1 tw:min-w-0 tw:flex tw:items-center",
                          style: isExpandedPreset ? { overflow: "auto" } : void 0,
                          children: /* @__PURE__ */ jsx(
                            PromptInputTextarea,
                            {
                              ref: textareaRef,
                              placeholder,
                              disabled,
                              value,
                              onChange: (e) => onChange == null ? void 0 : onChange(e.target.value),
                              rows: 1,
                              "data-expanded": isExpanded ? "true" : void 0,
                              style: {
                                minHeight: "auto",
                                maxHeight: maxHeight ?? Math.ceil(styles.fontSize * 1.5 * 5),
                                overflowY: "auto",
                                paddingTop: 0,
                                paddingBottom: 0,
                                paddingLeft: isExpanded ? 0 : styles.paddingX,
                                paddingRight: isExpanded ? 0 : 8,
                                transition: "padding 0.2s ease-out"
                              }
                            }
                          )
                        }
                      ),
                      /* @__PURE__ */ jsxs(
                        "div",
                        {
                          className: "tw:flex tw:items-center tw:shrink-0 concierge-prompt-input-icons",
                          "data-expanded": isExpanded ? "true" : void 0,
                          style: {
                            gap: responsiveStyles.iconGap,
                            paddingRight: isExpanded ? 0 : styles.paddingX,
                            paddingLeft: 0,
                            paddingBottom: 0,
                            justifyContent: isExpanded ? "flex-end" : void 0,
                            transition: "all 0.2s ease-out"
                          },
                          children: [
                            styles.showAttachmentButton && /* @__PURE__ */ jsx(
                              AttachmentButton,
                              {
                                disabled: disabled || isStreaming,
                                styles: responsiveStyles,
                                resolveColor
                              }
                            ),
                            styles.showMicrophoneButton && /* @__PURE__ */ jsx(
                              MicrophoneButton,
                              {
                                disabled: disabled || isStreaming,
                                textareaRef,
                                onChange,
                                styles: responsiveStyles,
                                themeIcons,
                                resolveColor
                              }
                            ),
                            /* @__PURE__ */ jsx(
                              SubmitButton,
                              {
                                disabled: disabled || !isStreaming && !(value == null ? void 0 : value.trim()) && !hasAttachments,
                                isStreaming,
                                onStop,
                                styles: responsiveStyles,
                                resolveColor
                              }
                            )
                          ]
                        }
                      )
                    ]
                  }
                )
              ] })
            }
          )
        ]
      }
    ) });
  } });
};
export {
  PromptInputConcierge
};
//# sourceMappingURL=index.js.map
